import { Button, StatusPill, TextAreaField } from "@flexfreight/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { api, ApiError } from "../../api/client";

type Msg = { role: "user" | "assistant"; content: string; citations?: { label: string; href: string }[]; mode?: string };
type Status = { mode: "live" | "demonstration"; model: string | null; tools: { name: string; description: string }[]; canWrite: false };
const SUGGESTIONS = ["How are we doing this week?", "Any shipment exceptions?", "Which tickets are open?", "Find customer Acme"];
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** Insights > Flex AI: questions answered from console data you're allowed to see. It never writes or sends. */
export function FlexAiPage() {
  const status = useQuery({ queryKey: ["console", "ai"], queryFn: () => api<Status>("/v1/console/ai") });
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [draft, setDraft] = useState("");
  const end = useRef<HTMLDivElement>(null);
  const chat = useMutation({
    mutationFn: (history: Msg[]) => api<{ mode: string; text: string; citations: { label: string; href: string }[]; tools: string[] }>("/v1/console/ai/chat", { method: "POST", body: { messages: history.map(({ role, content }) => ({ role, content })) } }),
    onSuccess: (r) => setMsgs((m) => [...m, { role: "assistant", content: r.text, citations: r.citations, mode: r.mode }]),
    onError: (e) => setMsgs((m) => [...m, { role: "assistant", content: `Couldn't answer: ${message(e)}` }]),
  });
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end", behavior: "smooth" });
  }, [msgs.length, chat.isPending]);
  const send = (text: string) => {
    const t = text.trim();
    if (!t || chat.isPending) return;
    const history: Msg[] = [...msgs, { role: "user", content: t }];
    setMsgs(history);
    setDraft("");
    chat.mutate(history);
  };
  const s = status.data;
  return (
    <section className="ffa-page">
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">Flex AI</h1>
          <p className="ffa-head__sub">Ask about customers, shipments, tickets and the business. It reads only what your role can see, and it never sends, books or changes anything.</p>
        </div>
        {s ? <StatusPill tone={s.mode === "live" ? "success" : "neutral"}>{s.mode === "live" ? `Live · ${s.model}` : "Demonstration mode"}</StatusPill> : null}
      </header>
      {s?.mode === "demonstration" ? (
        <div className="ffa-notice" role="note">
          <span className="ffa-small">Demonstration mode: no AI key is connected, so answers come from simple keyword matching over the same read-only tools. Connect an Anthropic key in Admin Center to turn on the assistant.</span>
        </div>
      ) : null}
      <div className="ffx-card ffa-card ffa-chat">
        <ol className="ffa-chat__log" aria-label="Conversation" aria-live="polite">
          {msgs.length === 0 ? (
            <li className="ffa-chat__empty">
              <p className="ffa-muted">Try one of these:</p>
              <div className="ffa-integration__actions">
                {SUGGESTIONS.map((q) => (
                  <Button key={q} size="sm" onClick={() => send(q)}>{q}</Button>
                ))}
              </div>
            </li>
          ) : null}
          {msgs.map((m, i) => (
            <li key={i} className={`ffa-chat__msg ffa-chat__msg--${m.role}`}>
              <span className="ffx-micro">{m.role === "user" ? "You" : "Flex AI"}</span>
              <p className="ffa-msg__body">{m.content}</p>
              {m.citations?.length ? (
                <ul className="ffa-chat__cites" aria-label="Sources">
                  {m.citations.map((c) => (
                    <li key={c.href}><Link to={c.href}>{c.label}</Link></li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
          {chat.isPending ? <li className="ffa-chat__msg ffa-chat__msg--assistant ffa-muted">Thinking…</li> : null}
        </ol>
        <div ref={end} />
        <form
          className="ffa-chat__compose"
          onSubmit={(e) => {
            e.preventDefault();
            send(draft);
          }}
        >
          <TextAreaField
            label="Ask Flex AI"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            maxLength={8000}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(draft);
              }
            }}
          />
          <Button type="submit" variant="primary" disabled={!draft.trim()} loading={chat.isPending}>Ask</Button>
        </form>
      </div>
      {s ? (
        <details className="ffa-small ffa-muted">
          <summary>What Flex AI can read for you ({s.tools.length} tools)</summary>
          <ul className="ffa-plainlist">
            {s.tools.map((t) => (
              <li key={t.name}><strong>{t.name.replace(/_/g, " ")}</strong>: {t.description}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
